'use client';

import type { QuickFilterItem } from '@weberus/shared-types';
import { RectangleButton, DotBadge } from '@yanolja-rnd/nol-design-system';
import { IcCalendarcheck, IcSorting, IcFilter } from '@yanolja-rnd/nol-icons';
import { InView } from 'react-intersection-observer';
import { cn } from '@/app/_shared/utils/tw-cn';
import { FILTER_LABEL, SORT_LABEL } from '@/app/(pages)/results/constants';
import { QuickFilterChips } from '@/app/(pages)/results/(pages)/ui/search-header/quick-filter-chips';

export interface AccommodationFilter {
  tab: 'LOCAL_ACCOMMODATION' | 'GLOBAL_ACCOMMODATION';
  fixedAreaRef: (el: HTMLDivElement | null) => void;
  flexibleAreaRef: (el: HTMLDivElement | null) => void;
  hasGlobalFilter: boolean;
  hasSortItems: boolean;
  hasQuickFilter: boolean;
  isFilterSelected: boolean;
  isSortSelected: boolean;
  checkInOutCapacityText: string;
  globalCheckInOutCapacityText: string;
  onClickCheckIODate?: () => void;
  onClickGlobalFilter?: () => void;
  onClickSort?: () => void;
  onClickChip: (item: QuickFilterItem) => void;
  onImpressionChip?: (item: QuickFilterItem & Partial<{ isGlobalFilter: boolean }>) => void;
  onImpressionSort?: () => void;
}
export const AccommodationFilter = (props: AccommodationFilter) => {
  const {
    tab,
    fixedAreaRef,
    flexibleAreaRef,
    hasQuickFilter,
    hasGlobalFilter,
    hasSortItems,
    isFilterSelected,
    isSortSelected,
    checkInOutCapacityText,
    globalCheckInOutCapacityText,
    onClickCheckIODate,
    onClickGlobalFilter,
    onClickSort,
    onClickChip,
    onImpressionChip,
    onImpressionSort,
  } = props;

  return (
    <div className="mx-auto max-w-legacy-pc-size z-middle">
      <div ref={fixedAreaRef} className="relative flex gap-4 bg-static-white px-16 pb-10 pt-12">
        {/* 체크인아웃/인원 버튼 */}
        <div className="flex flex-1 overflow-hidden">
          {tab === 'LOCAL_ACCOMMODATION' && (
            <RectangleButton
              theme="outlined_basic"
              icon={<IcCalendarcheck className="mr-2 flex-shrink-0" height={16} width={16} />}
              size="medium"
              weight="regular"
              className={cn('w-full px-6 pc:w-[366px]', {
                '!w-full': !hasGlobalFilter && !hasSortItems,
              })}
              isCapsule
              onClick={onClickCheckIODate}>
              <p className="mr-2 truncate typography-body-14-regular-line-clamp-1">{checkInOutCapacityText}</p>
            </RectangleButton>
          )}
          {tab === 'GLOBAL_ACCOMMODATION' && (
            <RectangleButton
              theme="outlined_basic"
              className="w-full px-6 pc:w-[366px]"
              icon={<IcCalendarcheck className="mr-2 flex-shrink-0" height={16} width={16} />}
              size="medium"
              weight="regular"
              isCapsule
              onClick={onClickCheckIODate}>
              <p className="mr-2 truncate typography-body-14-regular-line-clamp-1">{globalCheckInOutCapacityText}</p>
            </RectangleButton>
          )}
        </div>
        {/* 필터 및 정렬 버튼 */}
        {hasGlobalFilter && hasSortItems && (
          <div className="flex justify-end gap-4">
            <div className="relative">
              <RectangleButton
                icon={<IcFilter />}
                theme="outlined_basic"
                size="medium"
                weight="regular"
                showDotBadge={false}
                className="min-w-69 px-12 pc:w-[181px]"
                isCapsule
                onClick={onClickGlobalFilter}>
                {FILTER_LABEL}
              </RectangleButton>
              {isFilterSelected && (
                <div className="absolute right-12 top-2 size-11 translate-x-full rounded-capsule bg-static-white">
                  <DotBadge className="relative right-[3.5px] top-2 size-6" />
                </div>
              )}
            </div>
            <InView as="div" className="relative" triggerOnce onChange={(inView) => inView && onImpressionSort?.()}>
              <RectangleButton
                icon={<IcSorting />}
                theme="outlined_basic"
                size="medium"
                weight="regular"
                showDotBadge={false}
                className="min-w-69 px-12 pc:w-[181px]"
                isCapsule
                onClick={onClickSort}>
                {SORT_LABEL}
              </RectangleButton>
              {isSortSelected && (
                <div className="absolute right-12 top-2 size-11 translate-x-full rounded-capsule bg-static-white">
                  <DotBadge className="relative right-[3.5px] top-2 size-6" />
                </div>
              )}
            </InView>
          </div>
        )}
      </div>
      {/* 퀵 필터 */}
      {hasQuickFilter && (
        <div ref={flexibleAreaRef} className="flex w-full flex-col items-center">
          <div className="h-46 w-full max-w-legacy-pc-size">
            <QuickFilterChips onClickChip={onClickChip} onImpressionChip={onImpressionChip} />
          </div>
        </div>
      )}
    </div>
  );
};
